(() => {
  if (customElements.get('cosmic-site-header')) return;

  class CosmicSiteBase extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.addEventListener('submit', event => {
        if (!event.target.matches('.cms-localization')) return;
        // Preserve alternate-template previews when changing currency/language.
        const returnTo = event.target.querySelector('[name="return_to"]');
        if (returnTo) returnTo.value = location.pathname + location.search + location.hash;
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() { this.controller?.abort(); }
  }

  class CosmicSiteHeader extends CosmicSiteBase {
    connectedCallback() {
      super.connectedCallback();
      const signal = this.controller.signal;
      this.section = this.closest('.cms-header-section');
      this.previousY = window.scrollY;
      this.frame = null;
      this.openDialog = null;
      this.querySelectorAll('[data-cms-open]').forEach(button => { button.hidden = false; });
      this.addEventListener('click', event => {
        const trigger = event.target.closest('[data-cms-open]');
        if (trigger) this.showDialog(trigger);
        if (event.target.closest('[data-cms-close]')) event.target.closest('dialog')?.close();
        // Only treat a click outside the panel bounds as a backdrop click.
        if (event.target instanceof HTMLDialogElement) {
          const box = event.target.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) event.target.close();
        }
        const link = event.target.closest('a');
        if (link && link.closest('dialog')) this.openDialog?.close();
      }, { signal });
      this.querySelectorAll('dialog').forEach(dialog => {
        dialog.addEventListener('close', () => this.finishDialog(dialog), { signal });
      });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.openDialog) {
          event.preventDefault();
          this.openDialog.close();
          return;
        }
        if (event.key === 'Tab' && this.openDialog) {
          const controls = [...this.openDialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex="0"]')].filter(el => el.getClientRects().length);
          const first = controls[0], last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
        }
        if (event.key !== 'Escape' || this.openDialog) return;
        const details = event.target.closest('details[open]');
        if (details) { details.open = false; details.querySelector('summary').focus(); event.preventDefault(); }
      }, { signal });
      document.addEventListener('click', event => {
        this.querySelectorAll('.cms-desktop-nav details[open]').forEach(details => {
          if (!details.contains(event.target)) details.open = false;
        });
      }, { signal });
      this.addEventListener('focusin', () => this.section?.classList.remove('cms-is-hidden'), { signal });
      this.section?.classList.toggle('cms-is-sticky', this.dataset.sticky !== 'none');
      this.resizeObserver = new ResizeObserver(() => {
        document.documentElement.style.setProperty('--header-height', `${this.section?.offsetHeight || 0}px`);
        if (window.innerWidth >= 1200 && this.openDialog?.classList.contains('cms-drawer')) this.openDialog.close();
      });
      this.resizeObserver.observe(this);
      window.addEventListener('scroll', () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          const y = Math.max(0, window.scrollY);
          if (this.dataset.sticky === 'on-scroll-up') {
            const protectedState = this.openDialog || this.contains(document.activeElement) || this.querySelector('details[open]');
            if (y < this.section.offsetHeight || protectedState || y < this.previousY - 3) this.section.classList.remove('cms-is-hidden');
            else if (y > this.previousY + 3) this.section.classList.add('cms-is-hidden');
          }
          this.previousY = y;
          this.frame = null;
        });
      }, { passive: true, signal });
    }
    showDialog(trigger) {
      const dialog = document.getElementById(trigger.dataset.cmsOpen);
      if (!dialog || this.openDialog) return;
      this.trigger = trigger;
      this.openDialog = dialog;
      this.section?.classList.remove('cms-is-hidden');
      this.savedBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      trigger.setAttribute('aria-expanded', 'true');
      dialog.showModal();
      if (dialog.classList.contains('cms-search-dialog')) dialog.querySelector('input[type="search"]').focus();
    }
    finishDialog(dialog) {
      if (this.openDialog !== dialog) return;
      document.body.style.overflow = this.savedBodyOverflow;
      this.trigger?.setAttribute('aria-expanded', 'false');
      if (this.trigger?.isConnected && this.trigger.getClientRects().length) this.trigger.focus({ preventScroll: true });
      this.openDialog = null;
      this.trigger = null;
      this.previousY = window.scrollY;
    }
    disconnectedCallback() {
      const dialog = this.openDialog;
      if (dialog) { dialog.close(); this.finishDialog(dialog); }
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.section?.classList.remove('cms-is-sticky', 'cms-is-hidden');
      super.disconnectedCallback();
    }
  }
  customElements.define('cosmic-site-header', CosmicSiteHeader);
  customElements.define('cosmic-site-footer', class extends CosmicSiteBase {});
})();
